import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { residentialServices, commercialServices } from '../mockData';
import { CalendarIcon, CreditCard, Home, Building2, Bath, Maximize2, Loader2, RefreshCw, DoorOpen } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Recurring service options
const recurringOptions = [
  { id: 'one-time', name: 'One-Time Service', description: 'Single cleaning service' },
  { id: 'weekly', name: 'Weekly', description: 'Every week' },
  { id: 'bi-weekly', name: 'Bi-Weekly', description: 'Every 2 weeks' },
  { id: 'monthly', name: 'Monthly', description: 'Once a month' },
];

const Booking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const preSelectedService = location.state?.selectedService;
  const preSelectedCategory = location.state?.selectedCategory;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    category: preSelectedCategory || '',
    service: preSelectedService || '',
    bedrooms: '',
    bathrooms: '',
    sqft: '',
    recurring: 'one-time',
    timesPerWeek: '1',
    firstTimeCustomer: false,
    date: null,
    time: '',
    notes: ''
  });

  // Get services based on selected category
  const availableServices = formData.category === 'residential' 
    ? residentialServices 
    : formData.category === 'commercial' 
      ? commercialServices 
      : [];

  const selectedServiceData = [...residentialServices, ...commercialServices].find(s => s.id === formData.service);
  const selectedRecurring = recurringOptions.find(r => r.id === formData.recurring) || recurringOptions[0];
  const isCommercial = formData.category === 'commercial';
  
  // Calculate price per visit based on rooms/bedrooms, bathrooms, and square footage
  const calculatePricePerVisit = () => {
    if (!formData.service || !formData.bedrooms || !formData.bathrooms || !formData.sqft) return 0;
    
    const rooms = parseInt(formData.bedrooms);
    const bathrooms = parseFloat(formData.bathrooms);
    const sqft = parseInt(formData.sqft);
    
    // Get pricing from service data
    if (!selectedServiceData || !selectedServiceData.pricing) return 0;
    
    const { oneTime, weekly, biWeekly, monthly, bedroom, room, bathroom, sqft: sqftRate } = selectedServiceData.pricing;
    const frequencyMap = { 'one-time': oneTime, 'weekly': weekly, 'bi-weekly': biWeekly, 'monthly': monthly };
    const base = frequencyMap[formData.recurring] || oneTime;
    const roomRate = bedroom || room || 0;
    
    const roomPrice = rooms * roomRate;
    const bathroomPrice = bathrooms * bathroom;
    const sqftPrice = sqft * sqftRate;
    const totalPrice = base + roomPrice + bathroomPrice + sqftPrice;
    
    return totalPrice.toFixed(2);
  };

  const pricePerVisit = calculatePricePerVisit();
  const timesPerWeek = formData.recurring !== 'one-time' ? parseInt(formData.timesPerWeek) : 1;
  const weeklyTotal = pricePerVisit > 0 ? (parseFloat(pricePerVisit) * timesPerWeek).toFixed(2) : '0.00';
  const firstTimeDiscount = formData.firstTimeCustomer ? (parseFloat(weeklyTotal) * 0.10).toFixed(2) : '0.00';
  const afterDiscount = (parseFloat(weeklyTotal) - parseFloat(firstTimeDiscount)).toFixed(2);
  const TAX_RATE = 0.13; // 13% Ontario HST
  const taxAmount = afterDiscount > 0 ? (parseFloat(afterDiscount) * TAX_RATE).toFixed(2) : '0.00';
  const totalPrice = afterDiscount > 0 ? (parseFloat(afterDiscount) + parseFloat(taxAmount)).toFixed(2) : '0.00';
  
  // Debug logging
  console.log('Form Data:', formData);
  console.log('Price Per Visit:', pricePerVisit, 'Times/Week:', timesPerWeek, 'Weekly Total:', weeklyTotal, 'Tax:', taxAmount, 'Total:', totalPrice);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      // Reset service when category changes
      if (field === 'category') {
        return { ...prev, [field]: value, service: '', bedrooms: '', bathrooms: '', sqft: '', recurring: 'one-time', timesPerWeek: '1' };
      }
      // Reset recurring to one-time if service doesn't support recurring
      if (field === 'service') {
        const svc = [...residentialServices, ...commercialServices].find(s => s.id === value);
        if (svc && svc.pricing.monthly === null) {
          return { ...prev, [field]: value, recurring: 'one-time', timesPerWeek: '1' };
        }
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.address || 
        !formData.category || !formData.service || !formData.bedrooms || !formData.bathrooms || !formData.sqft ||
        !formData.date || !formData.time) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare booking data
      const bookingData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        category: formData.category,
        service: selectedServiceData?.name || formData.service,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        sqft: formData.sqft,
        recurring: formData.recurring,
        times_per_week: formData.recurring !== 'one-time' ? parseInt(formData.timesPerWeek) : 1,
        date: format(formData.date, 'PPP'),
        time: formData.time,
        notes: formData.notes,
        base_price: parseFloat(pricePerVisit),
        weekly_total: parseFloat(weeklyTotal),
        first_time_customer: formData.firstTimeCustomer,
        first_time_discount: parseFloat(firstTimeDiscount),
        price: parseFloat(totalPrice)
      };

      console.log('Submitting booking:', bookingData);

      // Create booking
      const bookingResponse = await axios.post(`${API}/bookings`, bookingData);
      console.log('Booking created:', bookingResponse.data);

      // Immediately create Stripe checkout session
      const originUrl = window.location.origin;
      console.log('Creating Stripe session for booking:', bookingResponse.data.booking_id);
      
      const stripeResponse = await axios.post(`${API}/payments/checkout`, {
        booking_id: bookingResponse.data.booking_id,
        origin_url: originUrl
      });

      console.log('Stripe session created:', stripeResponse.data);

      if (stripeResponse.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = stripeResponse.data.url;
      } else {
        throw new Error('No checkout URL received from Stripe');
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: "Booking Failed",
        description: error.response?.data?.detail || error.message || "Failed to create booking. Please try again.",
        variant: "destructive"
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 py-12 relative">
      {/* Background Image */}
      <div 
        className="fixed inset-0 z-0 opacity-10"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758273238564-806f750a2cce?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Book Your Cleaning</h1>
          <p className="text-lg text-gray-600">Fill in the details and we&apos;ll take care of the rest</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
            <CardTitle className="text-2xl">Booking Details</CardTitle>
            <CardDescription className="text-blue-100">Complete the form below to schedule your service</CardDescription>
          </CardHeader>
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Category Selection */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Select Service Type</h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Residential Option */}
                  <div
                    onClick={() => handleInputChange('category', 'residential')}
                    className={`cursor-pointer rounded-xl border-2 transition-all overflow-hidden ${
                      formData.category === 'residential'
                        ? 'border-blue-500 shadow-lg'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="h-28 relative overflow-hidden">
                      <img src="https://images.unsplash.com/photo-1758273238370-3bc08e399620?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400" alt="Residential" className="w-full h-full object-cover" />
                      <div className={`absolute inset-0 ${formData.category === 'residential' ? 'bg-blue-600/50' : 'bg-blue-900/40'}`}></div>
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                        <Home className="w-8 h-8 mb-1" />
                        <h4 className="font-bold text-lg">Residential</h4>
                        <p className="text-xs text-white/80">Homes & Apartments</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Commercial Option */}
                  <div
                    onClick={() => handleInputChange('category', 'commercial')}
                    className={`cursor-pointer rounded-xl border-2 transition-all overflow-hidden ${
                      formData.category === 'commercial'
                        ? 'border-yellow-500 shadow-lg'
                        : 'border-gray-200 hover:border-yellow-300'
                    }`}
                  >
                    <div className="h-28 relative overflow-hidden">
                      <img src="https://images.unsplash.com/photo-1770992176317-382f51aff35d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400" alt="Commercial" className="w-full h-full object-cover" />
                      <div className={`absolute inset-0 ${formData.category === 'commercial' ? 'bg-yellow-600/50' : 'bg-yellow-900/40'}`}></div>
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                        <Building2 className="w-8 h-8 mb-1" />
                        <h4 className="font-bold text-lg">Commercial</h4>
                        <p className="text-xs text-white/80">Offices & Businesses</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Commercial Quote Note */}
              {formData.category === 'commercial' && (
                <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4" data-testid="commercial-quote-note">
                  <p className="text-gray-800 font-medium text-sm">
                    For an accurate commercial cleaning quote, please call, text, or email us:
                  </p>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm">
                    <a href="tel:4372636134" className="text-yellow-700 font-semibold hover:underline">
                      (437) 263-6134
                    </a>
                    <a href="mailto:ateammentainance@gmail.com" className="text-yellow-700 font-semibold hover:underline">
                      ateammentainance@gmail.com
                    </a>
                  </div>
                </div>
              )}

              {/* Service Selection - Only shown after category is selected */}
              {formData.category && (
                <div className="space-y-4 border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {isCommercial ? 'Select Commercial Service' : 'Select Residential Service'}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {availableServices.map((service) => (
                      <div
                        key={service.id}
                        onClick={() => handleInputChange('service', service.id)}
                        className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${
                          formData.service === service.id
                            ? isCommercial 
                              ? 'border-yellow-500 bg-yellow-50'
                              : 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="font-semibold text-sm">{service.name}</div>
                        <div className="text-xs text-gray-500 mt-1 line-clamp-2">{service.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact Information */}
              <div className="space-y-4 border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900">Contact Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="(416) 555-0123"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Service Address *</Label>
                    <Input
                      id="address"
                      placeholder="123 Main St, Toronto, ON"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Property Details - Only shown after service is selected */}
              {formData.service && (
                <div className="space-y-4 border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900">Property Details</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="bedrooms" className="flex items-center gap-2">
                        {isCommercial ? <DoorOpen className="w-4 h-4 text-yellow-600" /> : <Home className="w-4 h-4 text-blue-600" />}
                        {isCommercial ? 'Number of Rooms *' : 'Bedrooms *'}
                      </Label>
                      <Select 
                        value={formData.bedrooms} 
                        onValueChange={(value) => handleInputChange('bedrooms', value)}
                      >
                        <SelectTrigger id="bedrooms">
                          <SelectValue placeholder={isCommercial ? "Select rooms" : "Select bedrooms"} />
                        </SelectTrigger>
                        <SelectContent>
                          {isCommercial ? (
                            <>
                              <SelectItem value="1">1 Room</SelectItem>
                              <SelectItem value="2">2 Rooms</SelectItem>
                              <SelectItem value="3">3 Rooms</SelectItem>
                              <SelectItem value="4">4 Rooms</SelectItem>
                              <SelectItem value="5">5 Rooms</SelectItem>
                              <SelectItem value="6">6 Rooms</SelectItem>
                              <SelectItem value="7">7 Rooms</SelectItem>
                              <SelectItem value="8">8 Rooms</SelectItem>
                              <SelectItem value="9">9 Rooms</SelectItem>
                              <SelectItem value="10">10 Rooms</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="1">1 Bedroom</SelectItem>
                              <SelectItem value="2">2 Bedrooms</SelectItem>
                              <SelectItem value="3">3 Bedrooms</SelectItem>
                              <SelectItem value="4">4 Bedrooms</SelectItem>
                              <SelectItem value="5">5+ Bedrooms</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bathrooms" className="flex items-center gap-2">
                        <Bath className={`w-4 h-4 ${isCommercial ? 'text-yellow-600' : 'text-blue-600'}`} />
                        {isCommercial ? 'Restrooms *' : 'Bathrooms *'}
                      </Label>
                      <Select 
                        value={formData.bathrooms} 
                        onValueChange={(value) => handleInputChange('bathrooms', value)}
                      >
                        <SelectTrigger id="bathrooms">
                          <SelectValue placeholder={isCommercial ? "Select restrooms" : "Select bathrooms"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 {isCommercial ? 'Restroom' : 'Bathroom'}</SelectItem>
                          <SelectItem value="1.5">1.5 {isCommercial ? 'Restrooms' : 'Bathrooms'}</SelectItem>
                          <SelectItem value="2">2 {isCommercial ? 'Restrooms' : 'Bathrooms'}</SelectItem>
                          <SelectItem value="2.5">2.5 {isCommercial ? 'Restrooms' : 'Bathrooms'}</SelectItem>
                          <SelectItem value="3">3 {isCommercial ? 'Restrooms' : 'Bathrooms'}</SelectItem>
                          <SelectItem value="4">4+ {isCommercial ? 'Restrooms' : 'Bathrooms'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sqft" className="flex items-center gap-2">
                        <Maximize2 className={`w-4 h-4 ${isCommercial ? 'text-yellow-600' : 'text-blue-600'}`} />
                        Square Footage *
                      </Label>
                      <Input
                        id="sqft"
                        type="number"
                        placeholder="e.g., 1500"
                        value={formData.sqft}
                        onChange={(e) => handleInputChange('sqft', e.target.value)}
                        min="100"
                        max="50000"
                        required
                      />
                    </div>
                  </div>

                  {/* Recurring Service Selection - hidden for one-time only services */}
                  {selectedServiceData && selectedServiceData.pricing.monthly !== null ? (
                  <div className="space-y-3 mt-4">
                    <Label className="flex items-center gap-2 text-base">
                      <RefreshCw className={`w-4 h-4 ${isCommercial ? 'text-yellow-600' : 'text-blue-600'}`} />
                      Service Frequency
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {recurringOptions.map((option) => (
                        <div
                          key={option.id}
                          onClick={() => handleInputChange('recurring', option.id)}
                          className={`cursor-pointer p-3 rounded-lg border-2 transition-all ${
                            formData.recurring === option.id
                              ? isCommercial
                                ? 'border-yellow-500 bg-yellow-50'
                                : 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-semibold text-sm">{option.name}</div>
                          {option.id !== 'one-time' && (
                            <div className="text-green-600 text-xs font-bold mt-1">
                              Lower Rate
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {selectedRecurring.id !== 'one-time' && (
                      <p className="text-sm text-green-600 font-medium">
                        ✓ Recurring rate applied - lower price per visit
                      </p>
                    )}

                    {/* Times Per Week - shown when recurring is selected */}
                    {formData.recurring !== 'one-time' && (
                      <div className="space-y-2 mt-3">
                        <Label className="flex items-center gap-2">
                          <RefreshCw className={`w-4 h-4 ${isCommercial ? 'text-yellow-600' : 'text-blue-600'}`} />
                          How many times per week? *
                        </Label>
                        <Select
                          value={formData.timesPerWeek}
                          onValueChange={(value) => handleInputChange('timesPerWeek', value)}
                        >
                          <SelectTrigger data-testid="times-per-week-select">
                            <SelectValue placeholder="Select times per week" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 time per week</SelectItem>
                            <SelectItem value="2">2 times per week</SelectItem>
                            <SelectItem value="3">3 times per week</SelectItem>
                            <SelectItem value="4">4 times per week</SelectItem>
                            <SelectItem value="5">5 times per week</SelectItem>
                            <SelectItem value="6">6 times per week</SelectItem>
                            <SelectItem value="7">7 times per week</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  ) : (
                    <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <p className="text-sm text-gray-600 font-medium">This is a one-time service only.</p>
                    </div>
                  )}

                  {/* First Time Customer Discount */}
                  <div className="mt-4">
                    <label
                      className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.firstTimeCustomer
                          ? isCommercial ? 'border-yellow-500 bg-yellow-50' : 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="first-time-customer-toggle"
                    >
                      <input
                        type="checkbox"
                        checked={formData.firstTimeCustomer}
                        onChange={(e) => handleInputChange('firstTimeCustomer', e.target.checked)}
                        className="w-5 h-5 rounded accent-blue-600"
                      />
                      <div>
                        <span className="font-semibold text-gray-900">First Time Customer</span>
                        <span className="ml-2 text-green-600 font-bold text-sm bg-green-100 px-2 py-0.5 rounded-full">10% OFF</span>
                      </div>
                    </label>
                  </div>

                  {/* Price Preview */}
                  {parseFloat(pricePerVisit) > 0 && (
                    <div className={`${isCommercial ? 'bg-yellow-50 border-yellow-200' : 'bg-blue-50 border-blue-200'} border-2 rounded-lg p-4`}>
                      <div className="space-y-2">
                        {formData.recurring !== 'one-time' && (
                          <div className="flex justify-between items-center">
                            <span className="text-green-600 font-medium">Recurring Rate Applied</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700">Price per visit:</span>
                          <span className="text-lg text-gray-700">${pricePerVisit}</span>
                        </div>
                        {formData.recurring !== 'one-time' && timesPerWeek > 1 && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700">x {timesPerWeek} times/week:</span>
                            <span className="text-lg text-gray-700">${weeklyTotal}</span>
                          </div>
                        )}
                        {formData.firstTimeCustomer && parseFloat(firstTimeDiscount) > 0 && (
                          <div className="flex justify-between items-center text-green-600">
                            <span className="font-medium">First Time Discount (10%):</span>
                            <span className="font-bold">-${firstTimeDiscount}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">HST (13%):</span>
                          <span className="text-lg text-gray-600">${taxAmount}</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between items-center">
                          <span className="text-gray-900 font-bold">Total{formData.recurring !== 'one-time' ? '/week' : ''}:</span>
                          <span className={`text-2xl font-bold ${isCommercial ? 'text-yellow-600' : 'text-blue-600'}`}>${totalPrice}</span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1 mt-3 pt-3 border-t">
                        <p>• {formData.bedrooms} {isCommercial ? 'room(s)' : 'bedroom(s)'}</p>
                        <p>• {formData.bathrooms} {isCommercial ? 'restroom(s)' : 'bathroom(s)'}</p>
                        <p>• {formData.sqft} square feet</p>
                        <p>• {selectedRecurring.name} service{formData.recurring !== 'one-time' ? ` - ${timesPerWeek}x/week` : ''}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Schedule */}
              {formData.service && (
                <div className="space-y-4 border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900">Schedule</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Preferred Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.date ? format(formData.date, 'PPP') : 'Pick a date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.date}
                            onSelect={(date) => handleInputChange('date', date)}
                            disabled={(date) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              return date < today;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="time">Preferred Time *</Label>
                      <Select 
                        value={formData.time} 
                        onValueChange={(value) => handleInputChange('time', value)}
                      >
                        <SelectTrigger id="time">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="8:00 AM">8:00 AM</SelectItem>
                          <SelectItem value="10:00 AM">10:00 AM</SelectItem>
                          <SelectItem value="12:00 PM">12:00 PM</SelectItem>
                          <SelectItem value="2:00 PM">2:00 PM</SelectItem>
                          <SelectItem value="4:00 PM">4:00 PM</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              {formData.service && (
                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Any special instructions or requests..."
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    rows={3}
                  />
                </div>
              )}

              {/* Price Summary */}
              {parseFloat(pricePerVisit) > 0 && formData.date && formData.time && (
                <div className="border-t pt-6">
                  <div className={`${isCommercial ? 'bg-gradient-to-br from-yellow-500 to-yellow-600' : 'bg-gradient-to-br from-blue-600 to-blue-700'} p-6 rounded-lg text-white`}>
                    <div className="space-y-2 mb-3">
                      {formData.recurring !== 'one-time' && (
                        <div className="flex justify-between items-center bg-green-500/20 p-2 rounded">
                          <span className="text-green-200 font-medium">Recurring Rate Applied</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="opacity-90">Price per visit:</span>
                        <span className="text-lg">${pricePerVisit}</span>
                      </div>
                      {formData.recurring !== 'one-time' && timesPerWeek > 1 && (
                        <div className="flex justify-between items-center">
                          <span className="opacity-90">x {timesPerWeek} times/week:</span>
                          <span className="text-lg">${weeklyTotal}</span>
                        </div>
                      )}
                      {formData.firstTimeCustomer && parseFloat(firstTimeDiscount) > 0 && (
                        <div className="flex justify-between items-center bg-green-500/20 p-2 rounded">
                          <span className="text-green-200 font-medium">First Time Discount (20%):</span>
                          <span className="text-green-200 font-bold">-${firstTimeDiscount}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="opacity-90">HST (13%):</span>
                        <span className="text-lg">${taxAmount}</span>
                      </div>
                    </div>
                    <div className="border-t border-white/30 pt-3 flex justify-between items-center">
                      <span className="text-lg font-semibold">Total Amount{formData.recurring !== 'one-time' ? '/week' : ''}:</span>
                      <span className="text-4xl font-black">${totalPrice}</span>
                    </div>
                    <p className="text-sm opacity-90 mt-2">
                      {selectedRecurring.id !== 'one-time' 
                        ? `Recurring ${selectedRecurring.name.toLowerCase()} service (${timesPerWeek}x/week) - Payment via Stripe`
                        : 'Payment will be processed securely via Stripe'
                      }
                    </p>
                  </div>
                </div>
              )}

              <Button 
                type="submit" 
                size="lg" 
                className={`w-full font-bold py-6 text-lg ${
                  isCommercial 
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900'
                    : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                }`}
                disabled={parseFloat(pricePerVisit) === 0 || isSubmitting || !formData.date || !formData.time}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-5 w-5" />
                    Book & Pay Now
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Booking;
